import { supabase } from "@/integrations/supabase/client";
import { Assessment, Category, Question, Answer, AnswerType } from "@/types/assessment";

// Bulk map multiple database assessments to application models using batched queries
export const mapDbAssessmentsToModels = async (dbAssessments: any[]): Promise<Assessment[]> => {
  if (dbAssessments.length === 0) return [];

  const assessmentIds = dbAssessments.map((a) => a.id);

  // Fetch all categories for all assessments in one query
  const { data: allCategories, error: categoriesError } = await supabase
    .from("categories")
    .select("*")
    .in("assessment_id", assessmentIds)
    .order("order_index");

  if (categoriesError) {
    console.error("Error fetching categories:", categoriesError);
    throw categoriesError;
  }

  const categoryIds = (allCategories || []).map((c) => c.id);

  // Fetch all questions and answers in parallel
  const [questionsResult, versionHistoryResult] = await Promise.all([
    categoryIds.length > 0
      ? supabase.from("questions").select("*").in("category_id", categoryIds).order("order_index")
      : Promise.resolve({ data: [] as any[], error: null }),
    supabase
      .from("version_history")
      .select("*")
      .in("assessment_id", assessmentIds)
      .order("version_number", { ascending: false }),
  ]);

  if (questionsResult.error) {
    console.error("Error fetching questions:", questionsResult.error);
    throw questionsResult.error;
  }

  const allQuestions = questionsResult.data || [];
  const questionIds = allQuestions.map((q) => q.id);

  // Fetch all answers in one query
  let allAnswers: any[] = [];
  if (questionIds.length > 0) {
    const { data, error } = await supabase.from("answers").select("*").in("question_id", questionIds);
    if (error) {
      console.error("Error fetching answers:", error);
      throw error;
    }
    allAnswers = data || [];
  }

  const allVersionHistory = versionHistoryResult.data || [];

  // Index data by parent IDs for fast lookup
  const categoriesByAssessment = new Map<string, any[]>();
  for (const cat of allCategories || []) {
    const list = categoriesByAssessment.get(cat.assessment_id) || [];
    list.push(cat);
    categoriesByAssessment.set(cat.assessment_id, list);
  }

  const questionsByCategory = new Map<string, any[]>();
  for (const q of allQuestions) {
    const list = questionsByCategory.get(q.category_id) || [];
    list.push(q);
    questionsByCategory.set(q.category_id, list);
  }

  const answersByQuestion = new Map<string, any[]>();
  for (const a of allAnswers) {
    const list = answersByQuestion.get(a.question_id) || [];
    list.push(a);
    answersByQuestion.set(a.question_id, list);
  }

  const versionsByAssessment = new Map<string, any[]>();
  for (const v of allVersionHistory) {
    const list = versionsByAssessment.get(v.assessment_id) || [];
    list.push(v);
    versionsByAssessment.set(v.assessment_id, list);
  }

  // Assemble models in memory
  return dbAssessments.map((dbAssessment) => {
    const cats = categoriesByAssessment.get(dbAssessment.id) || [];

    const categories: Category[] = cats.map((categoryData) => {
      const qs = questionsByCategory.get(categoryData.id) || [];

      const questions: Question[] = qs.map((questionData) => {
        const ans = answersByQuestion.get(questionData.id) || [];

        const answers: Answer[] = ans.map((answerData) => ({
          id: answerData.id,
          text: answerData.text,
          value: answerData.value,
          recommendedActions:
            answerData.recommended_actions && typeof answerData.recommended_actions === "string"
              ? JSON.parse(answerData.recommended_actions)
              : Array.isArray(answerData.recommended_actions)
                ? answerData.recommended_actions
                : [],
        }));

        return {
          id: questionData.id,
          text: questionData.text,
          explanation: questionData.explanation,
          answerType: (questionData.answer_type as AnswerType) || "single",
          answers,
          required: questionData.required,
          premium: questionData.premium,
          disabled: questionData.disabled || false,
        };
      });

      return {
        id: categoryData.id,
        name: categoryData.name,
        description: categoryData.description,
        questions,
        order: categoryData.order_index,
      };
    });

    const versionHistoryData = versionsByAssessment.get(dbAssessment.id) || [];
    const versionHistory = versionHistoryData.map((versionData: any) => ({
      versionNumber: versionData.version_number,
      timestamp: versionData.created_at,
      categories:
        typeof versionData.categories_snapshot === "string"
          ? JSON.parse(versionData.categories_snapshot)
          : (versionData.categories_snapshot as unknown as Category[]),
    }));

    return {
      id: dbAssessment.id,
      title: dbAssessment.title,
      description: dbAssessment.description,
      categories,
      version: dbAssessment.version,
      createdAt: dbAssessment.created_at,
      updatedAt: dbAssessment.updated_at,
      isActive: dbAssessment.is_active,
      isMaturityModel: dbAssessment.is_maturity_model,
      slug: dbAssessment.slug,
      notifyEmails: dbAssessment.notify_emails,
      calendlyLink: dbAssessment.calendly_link,
      assessmentType: dbAssessment.assessment_type,
      status: dbAssessment.status,
      versionHistory,
    };
  });
};

// Single assessment mapper — delegates to bulk mapper
export const mapDbAssessmentToModel = async (dbAssessment: any): Promise<Assessment> => {
  const results = await mapDbAssessmentsToModels([dbAssessment]);
  return results[0];
};
